'use client'

/**
 * Scroll cue for full-screen (100vh) heroes.
 *
 * A viewport-height hero shows nothing of the page below it on load, so the
 * cue is the only signal that scrolling is worthwhile. Rendered by the
 * `full_bleed_media` and `centered_stacked` hero variants at `min_height:
 * '100vh'` unless the dealer unchecks "Show scroll cue" (`scroll_cue: false`).
 *
 * Client component purely for the click handler — no heavy imports, so the
 * client-reference edge it adds to the hero's server module is a few hundred
 * bytes, not a vendor chunk (see CLAUDE.md → "The client/server boundary").
 *
 * Scrolls to the bottom edge of the hero rather than to a named next section:
 * the dispatcher's section wrappers vary by kind, and the hero's own `<section>`
 * is the one element this button is always inside. A sticky header would cover
 * that edge, so its height is measured at click time and subtracted.
 */

import { useEffect, useRef, useState } from 'react'

/** Ceiling on the fold correction below. A layout that reports a wild overflow
 *  should leave the cue near the hero's foot, not floating up by the headline. */
const MAX_LIFT_PX = 160

export function HeroScrollCue({ dark }: { dark: boolean }) {
  const ref = useRef<HTMLButtonElement>(null)
  // A 100vh hero is a full viewport TALL, but it doesn't START at the viewport
  // top: an announcement bar (or any chrome in normal flow above it) pushes it
  // down, so its foot — and a cue pinned there — falls below the fold by
  // exactly that much. Measured rather than assumed, because the offending
  // chrome is per-tenant and dismissible.
  const [lift, setLift] = useState(0)

  useEffect(() => {
    const hero = ref.current?.closest('section')
    if (!hero) return
    const measure = () => {
      // Document coordinates, so the reading doesn't depend on scroll position.
      const heroBottom = hero.getBoundingClientRect().bottom + window.scrollY
      setLift(Math.min(Math.max(heroBottom - window.innerHeight, 0), MAX_LIFT_PX))
    }
    measure()
    // Body resize covers the announcement bar being dismissed; window resize
    // covers rotation and desktop drags.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  const scrollPastHero = () => {
    const hero = ref.current?.closest('section')
    if (!hero) return
    const header = document.querySelector('header')
    // Only a header that stays on screen eats into the landing position.
    const headerOffset =
      header && ['sticky', 'fixed'].includes(getComputedStyle(header).position)
        ? header.getBoundingClientRect().height
        : 0
    const top = hero.getBoundingClientRect().bottom + window.scrollY - headerOffset
    // `smooth` is honored per the user's OS motion setting by the browser
    // itself; no need to branch on prefers-reduced-motion here.
    window.scrollTo({ top, behavior: 'smooth' })
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={scrollPastHero}
      aria-label="Scroll to content"
      style={{ bottom: `calc(1.5rem + ${lift}px)` }}
      // Solid white on the dark side, never a faded one: alpha composites the
      // chevron back toward whatever is behind it, and `dark` knows the backdrop
      // is dark without knowing it is dark ENOUGH (see onDarkTextContrast.test).
      // The button is the hit target and the focus anchor only — it must NOT
      // animate, because the keyframes set `transform` and would clobber the
      // `-translate-x-1/2` that centers it (and a focus ring that drifts is
      // worse than no motion at all). The disc inside is what moves.
      className={`absolute left-1/2 -translate-x-1/2 z-10 grid h-12 w-12 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
        dark
          ? 'text-white focus-visible:ring-white focus-visible:ring-offset-transparent'
          : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] focus-visible:ring-[var(--color-cta-edge)] focus-visible:ring-offset-[var(--color-bg-primary)]'
      }`}
    >
      {/* The disc is the highlight: a bare chevron on a busy frame of video is
          easy to miss, and a translucent fill reads as an affordance without
          adding a solid chip to the composition. Fill + edge only — the glyph
          on top stays fully opaque, so this can only improve its contrast. */}
      <span
        className={`hero-scroll-cue__chevron grid h-11 w-11 place-items-center rounded-full ring-1 transition-colors ${
          dark
            ? 'bg-black/25 ring-white/30 hover:bg-black/40'
            : 'bg-black/[0.06] ring-black/10 hover:bg-black/10'
        }`}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          // A hairline shadow keeps the chevron readable where the disc happens
          // to sit over a bright patch of hero photo.
          style={dark ? { filter: 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.45))' } : undefined}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </span>
    </button>
  )
}
